import { createTheme } from '@mui/material/styles';
import { tokens, rgbToHex, type Mode, type PaletteToken } from './tokens';

/**
 * MUI theme — built per color mode from real token colors.
 *
 * Why real hex (not `rgb(var(--color-x))` / `color-mix(...)`):
 * MUI's JS color utilities (`decomposeColor`, used by `lighten` / `darken` /
 * `alpha` / `getContrastText`) only accept `#nnn`, `rgb()`, `rgba()`, `hsl()`,
 * `hsla()`, `color()`. A `var(...)` reference parses to NaN (silently broken
 * shades) and a `color-mix(...)` value THROWS ("Unsupported color"), which
 * blanks the whole screen the moment any component derives a tone — e.g.
 * `<Alert severity="warning">` runs `lighten(palette.warning.light, …)`.
 *
 * So the palette uses concrete hex values from `tokens[mode].palette`, and the
 * theme is rebuilt when the color mode changes (see `ThemedApp` in main.tsx).
 * `.light` / `.dark` shades are left for MUI to derive from each `.main` hex —
 * which now works because the inputs are real colors. Setting `palette.mode`
 * to the active mode also lets MUI's own dark-mode defaults (contrast, action
 * overlays, elevation) kick in.
 *
 * Component `styleOverrides` keep using `rgb(var(--color-x))` references: those
 * are written straight to CSS properties (never decomposed) and resolve to the
 * active mode via the `.dark` class on <html>, so they stay correct too.
 */

const cssVar = (name: string) => `rgb(var(--color-${name}))`;
const cssVarAlpha = (name: string, a: number) =>
  `rgb(var(--color-${name}) / ${a})`;

function buildPalette(mode: Mode) {
  const p = tokens[mode].palette;
  const hex = (k: PaletteToken) => rgbToHex(p[k]);
  return {
    mode,
    primary: { main: hex('primary'), contrastText: hex('primaryForeground') },
    secondary: { main: hex('secondary') },
    error: { main: hex('danger') },
    success: { main: hex('success') },
    warning: { main: hex('warning') },
    background: { default: hex('background'), paper: hex('surface') },
    text: { primary: hex('foreground'), secondary: hex('foregroundMuted') },
    divider: hex('border'),
  };
}

export function buildMuiTheme(mode: Mode) {
  return createTheme({
    palette: buildPalette(mode),
    shape: { borderRadius: parseInt(tokens.radii.lg, 10) },
    typography: {
      fontFamily: tokens.typography.sansFamily.join(','),
      htmlFontSize: tokens.typography.baseSize,
      h1: { fontWeight: 600, letterSpacing: tokens.typography.trackingTight },
      h2: { fontWeight: 600, letterSpacing: tokens.typography.trackingTight },
      h3: { fontWeight: 600, letterSpacing: tokens.typography.trackingTight },
      h4: { fontWeight: 600, letterSpacing: tokens.typography.trackingTight },
      h5: { fontWeight: 600, letterSpacing: tokens.typography.trackingTight },
      h6: { fontWeight: 600, letterSpacing: tokens.typography.trackingTight },
      button: { fontWeight: 500, letterSpacing: '-0.005em', textTransform: 'none' },
      body1: { letterSpacing: tokens.typography.trackingBase },
      body2: { letterSpacing: '-0.006em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: 'transparent' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: tokens.radii.lg,
            paddingInline: 18,
            paddingBlock: 10,
            fontWeight: 500,
            transition: `all ${tokens.motion.base}`,
          },
          sizeLarge: { paddingBlock: 12, fontSize: '0.95rem' },
          containedPrimary: {
            boxShadow: '0 1px 0 rgba(255,255,255,0.1) inset, 0 1px 2px rgba(0,0,0,0.2)',
            '&:hover': {
              boxShadow: '0 1px 0 rgba(255,255,255,0.15) inset, 0 4px 12px rgba(0,0,0,0.18)',
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'medium' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radii.lg,
            backgroundColor: cssVar('surface'),
            transition: `box-shadow ${tokens.motion.base}, border-color ${tokens.motion.base}`,
            fontSize: '0.95rem',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: cssVar('border') },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: cssVar('border-strong'),
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: cssVar('primary'),
              borderWidth: 1,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 4px ${cssVarAlpha('primary', 0.12)}`,
            },
            '& input::placeholder': {
              color: cssVar('foreground-muted'),
              opacity: 0.6,
            },
          },
          input: { paddingBlock: 13 },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: cssVar('foreground-muted'),
            '&.Mui-focused': { color: cssVar('primary') },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 2,
            marginTop: 6,
            fontSize: '0.78rem',
            letterSpacing: '-0.005em',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: tokens.radii.lg },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: tokens.radii.md },
        },
      },
      MuiDialog: {
        // Phone viewports (< 600px) get a full-bleed dialog regardless of the
        // dialog's React `maxWidth` / `fullWidth` props. CSS-only — keeps every
        // existing call site working without per-Dialog `fullScreen` plumbing.
        // Dialogs that already pass `fullScreen` keep their own behavior.
        styleOverrides: {
          paper: ({ theme }) => ({
            [theme.breakpoints.down('sm')]: {
              margin: 0,
              width: '100%',
              maxWidth: '100%',
              height: '100%',
              maxHeight: '100%',
              borderRadius: 0,
            },
          }),
        },
      },
    },
  });
}

/** Light-mode theme — used as a safe default by any non-reactive consumer. */
export const muiTheme = buildMuiTheme('light');
